import { useEffect, useState } from 'react';
import { Flame, Plus, MapPinned, ClipboardPaste, Sparkles, TriangleAlert, Loader2 } from 'lucide-react';
import { Pulse } from './Pulse';
import { useIncidentStore } from '@/incident/store';
import { bungendoreScenario } from '@/incident/scenario';
import { Field, TextInput } from '@/components/ui/controls';
import { LocationSearch, type LocationResult } from './LocationSearch';
import { CoveragePlanningView } from '@/components/response-coverage/CoveragePlanningView';
import { usePagerParse } from '@/hooks/usePagerParse';
import {
  INCIDENT_TYPES,
  INCIDENT_TYPE_LABELS,
  INCIDENT_LEVELS,
  INCIDENT_LEVEL_LABELS,
  type IncidentType,
  type IncidentLevel,
  type LngLat,
} from '@duty/shared';

/** Shown when there is no active incident: load the demo or create a blank one. */
export function EmptyState() {
  const { dispatch } = useIncidentStore();
  const [name, setName] = useState('');
  const [locationText, setLocationText] = useState('');
  const [coordinates, setCoordinates] = useState<LngLat | undefined>();
  const [incidentType, setIncidentType] = useState<IncidentType>('StructureFire');
  const [level, setLevel] = useState<IncidentLevel>('Level1');
  const [planningOpen, setPlanningOpen] = useState(false);
  const [pagerText, setPagerText] = useState('');
  const pagerPreview = usePagerParse(pagerText);

  const handleLocationChange = (result: LocationResult | string) => {
    if (typeof result === 'string') {
      setLocationText(result);
      setCoordinates(undefined);
    } else {
      setLocationText(result.place);
      setCoordinates(result.coordinates);
    }
  };

  // Any message, any format: as soon as a parse (deterministic or AI-assisted)
  // resolves, pre-fill the form below — the officer still reviews every field
  // before "Create incident" actually commits anything.
  useEffect(() => {
    if (pagerPreview.status !== 'done' || !pagerPreview.event) return;
    setName(pagerPreview.suggestedName);
    setLocationText(pagerPreview.effectiveLocation);
    setCoordinates(pagerPreview.effectiveCoordinates);
    setIncidentType(pagerPreview.effectiveIncidentType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagerPreview.status, pagerPreview.event]);

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="panel w-full max-w-lg p-6 text-center">
        <div className="mb-3 flex justify-center">
          <Pulse state="idle" size={72} />
        </div>
        <h1 className="text-lg font-semibold">Duty Officer Helper</h1>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted">
          Advisory decision support. Open the demo scenario, or start a new incident in standalone
          mode.
        </p>

        <button
          onClick={() => dispatch({ type: 'load-scenario' })}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent/15 px-4 py-2.5 text-sm font-semibold text-accent shadow-glow transition-colors hover:bg-accent/25"
        >
          <Flame size={16} /> Load demo · {bungendoreScenario.title}
        </button>

        <button
          onClick={() => setPlanningOpen(true)}
          className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-muted transition-colors hover:border-accent/40 hover:text-accent"
        >
          <MapPinned size={15} /> Open coverage planning
        </button>

        <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wide text-muted">
          <span className="h-px flex-1 bg-border" /> or paste a page <span className="h-px flex-1 bg-border" />
        </div>

        <div className="space-y-2 text-left">
          <label className="flex items-center gap-1.5 text-xs font-medium text-muted" htmlFor="pager-paste">
            <ClipboardPaste size={13} /> Paste an incident page (e.g. from LGDO) — parses as you paste
          </label>
          <textarea
            id="pager-paste"
            value={pagerText}
            onChange={(e) => setPagerText(e.target.value)}
            placeholder="LGDO - 26-121323 - Bush Fire - FIRECALL - TARAGO RD,HOPE DR,BUNGENDORE,QUEANBEYAN PALERANG (NSW),2621 - [149.462897,-35.219627]"
            rows={2}
            className="w-full rounded-md border border-border bg-surface-2 px-2.5 py-2 font-mono text-[11px] text-text placeholder:text-muted/60 focus:border-accent/60 focus:outline-none"
          />

          {pagerPreview.status === 'ai-pending' && (
            <p className="flex items-center gap-1.5 text-[11px] text-muted">
              <Loader2 size={12} className="animate-spin" /> Not a recognised page format — asking the AI model to
              help extract the details…
            </p>
          )}

          {pagerPreview.status === 'done' && pagerPreview.event && (
            <div className="rounded-md border border-accent/30 bg-accent/10 p-2.5 text-xs">
              {pagerPreview.aiExtracted && (
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                  <Sparkles size={11} /> AI-assisted extraction — please verify carefully
                </div>
              )}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-text">
                <span>
                  <span className="text-muted">Type: </span>
                  {INCIDENT_TYPE_LABELS[pagerPreview.effectiveIncidentType]}
                  {pagerPreview.event.typeText && (
                    <span className="text-muted"> (paged as "{pagerPreview.event.typeText}")</span>
                  )}
                </span>
                {pagerPreview.event.incidentNumber && (
                  <span>
                    <span className="text-muted">No: </span>
                    {pagerPreview.event.incidentNumber}
                  </span>
                )}
              </div>
              <p className="mt-1 text-text">{pagerPreview.effectiveLocation || 'No location text parsed'}</p>
              <p className="mt-1 text-[10px] text-muted">
                {pagerPreview.effectiveCoordinates
                  ? `Coordinates: ${pagerPreview.effectiveCoordinates.lat.toFixed(5)}, ${pagerPreview.effectiveCoordinates.lng.toFixed(5)}${
                      !pagerPreview.event.location ? ' (no address text — using coordinates as the location)' : ''
                    }`
                  : 'No coordinates in this message — location text only.'}
              </p>
              {pagerPreview.geocodeChecking && (
                <p className="mt-1 flex items-center gap-1 text-[10px] text-muted">
                  <Loader2 size={10} className="animate-spin" /> Checking address against paged coordinates…
                </p>
              )}
              {pagerPreview.geocodeDiscrepancyM !== undefined && (
                <p className="mt-1.5 flex items-start gap-1.5 rounded border border-med/40 bg-med/10 p-1.5 text-[10px] text-med">
                  <TriangleAlert size={12} className="mt-0.5 shrink-0" />
                  The paged coordinates are ~{pagerPreview.geocodeDiscrepancyM.toLocaleString()} m from where this
                  address geocodes to. The coordinate may reflect where the call was made rather than the address
                  entrance — confirm the location before responding.
                </p>
              )}
              <p className="mt-1.5 text-[10px] text-muted">
                Pre-filled below — review before creating the incident.
              </p>
            </div>
          )}
        </div>

        <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wide text-muted">
          <span className="h-px flex-1 bg-border" /> or new incident <span className="h-px flex-1 bg-border" />
        </div>

        <form
          className="space-y-3 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && locationText.trim())
              dispatch({
                type: 'new-incident',
                name: name.trim(),
                location: locationText.trim(),
                coordinates,
                incidentType,
                level,
              });
          }}
        >
          <Field label="Incident name">
            <TextInput value={name} onChange={setName} placeholder="e.g. Smith Street Incident" ariaLabel="Incident name" />
          </Field>
          <Field label="Location">
            <LocationSearch
              value={locationText}
              onChange={handleLocationChange}
              placeholder="Address, suburb or lat,lng…"
            />
          </Field>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted">Incident type</label>
            <div className="flex flex-wrap gap-1.5">
              {INCIDENT_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setIncidentType(t)}
                  className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                    incidentType === t
                      ? 'bg-accent/20 font-semibold text-accent border border-accent/40'
                      : 'border border-border text-text/70 hover:border-accent/40 hover:text-text'
                  }`}
                >
                  {INCIDENT_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted">Incident level</label>
            <div className="flex gap-1.5">
              {INCIDENT_LEVELS.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLevel(l)}
                  className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                    level === l
                      ? 'bg-accent/20 font-semibold text-accent border border-accent/40'
                      : 'border border-border text-text/70 hover:border-accent/40 hover:text-text'
                  }`}
                >
                  {INCIDENT_LEVEL_LABELS[l]}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={!name.trim() || !locationText.trim()}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-text hover:border-accent/60 disabled:opacity-40"
          >
            <Plus size={15} /> Create incident
          </button>
        </form>
      </div>
      {planningOpen && (
        <CoveragePlanningView
          onClose={() => setPlanningOpen(false)}
          onCreateIncidentAt={(point) => {
            dispatch({
              type: 'new-incident',
              name: 'Modelled incident',
              location: `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`,
              coordinates: point,
            });
            setPlanningOpen(false);
          }}
        />
      )}
    </div>
  );
}
